import {emptyMediaMixin} from "@/domain/galleries/mixins";
import {galleryTypes} from "@/domain/galleries/constants";


const flattenGalleries = galleries =>
    galleries.reduce((acc, {sub_galleries, ...mainGallery}) =>
        [...acc, mainGallery, ...sub_galleries], [])


const state = {
    galleries: {},
}

const getters = {
    galleries: state => state.galleries,
    paidGalleries: state => state.galleries && state.galleries[galleryTypes.PAID],
    unlockedGalleries: state => state.galleries && state.galleries[galleryTypes.UNLOCKED],
    favoriteGalleries: state => state.galleries && state.galleries[galleryTypes.FAVORITE],
    paidGalleriesMediasCount: state => {
        if (!state.galleries || !state.galleries[galleryTypes.PAID]) {
            return 0
        }
        return state.galleries[galleryTypes.PAID].reduce((acc, gallery) => acc + gallery.media_count, 0)
    },
    unlockedGalleriesMediasCount: state => {
        if (!state.galleries || !state.galleries[galleryTypes.UNLOCKED]) {
            return 0
        }
        return state.galleries[galleryTypes.UNLOCKED].reduce((acc, gallery) => acc + gallery.media_count, 0)
    },
    favoriteGalleriesMediasCount: state => {
        if (!state.galleries || !state.galleries[galleryTypes.FAVORITE]) {
            return 0
        }
        return state.galleries[galleryTypes.FAVORITE].reduce((acc, gallery) => acc + gallery.media_count, 0)
    },
    totalMediasCount: state => {
        if (!state.galleries) {
            return 0
        }
        return Object.values(state.galleries).flat().reduce((acc, gallery) => acc + gallery.media_count, 0)
    },
    priceList: (state) => ({items, galleryId}) => {
        if (!items && galleryId) {
            const gallery = Object.values(state.galleries).flat().find(gallery => gallery.id === galleryId);
            if (!gallery || !gallery.price_list || !gallery.price_list.items) return false;
            items = gallery.price_list.items;
        }
        if (!items || !items.length) return false;
        let prices = items.filter(item => item.unit_price || item.price);
        // if is range price list
        if (prices.some(item => item.from_qty && item.to_qty)) {
            prices = prices.map(item => ({
                key: `${item.from_qty}-${item.to_qty}`,
                from_qty: item.from_qty,
                to_qty: item.to_qty,
                price: item.unit_price,
                price_original: item.unit_price,
            }));
        } else {
            prices = prices.map(item => ({
                key: item.qty,
                qty: item.qty,
                price: item.price || Math.round(item.unit_price * item.qty * 10) / 10,
                price_original: item.price_original || Math.round(item.unit_price * item.qty * 10) / 10
            }));
        }
        if (prices.length > 0) prices[prices.length - 1].last = true;
        return prices;
    }
}

const mutations = {
    setGalleries(state, galleries) {
        Object.values(galleryTypes).forEach(type => {
            // Flatten sub-galleries
            const flattenedGalleries = flattenGalleries(galleries[type]);
            // Set empty medias for each gallery
            flattenedGalleries.forEach(gallery => {
                const num_placeholder = Math.min(gallery.media_count, 20)
                gallery.medias = emptyMediaMixin(num_placeholder);
            });
            galleries[type] = flattenedGalleries;
            // remove galleries with no media and no price list
            galleries[type] = galleries[type].filter(gallery => gallery.media_count > 0 || (
                gallery.price_list
                && gallery.price_list.items
                && gallery.price_list.items.length > 0)
            )
        });
        state.galleries = galleries;
    },
    setMedias(state, {gallery_type, gallery_id, append = true, medias}) {
        const gallery = state.galleries[gallery_type].find(gallery => gallery.id === gallery_id)

        // remove placeholders
        if (gallery.medias.results.find(media => media.is_placeholder)) {
            gallery.medias.results = gallery.medias.results.filter(media => media.is_placeholder === false)
        }

        if (append) {
            // load more pagination
            gallery.medias.results = gallery.medias.results.concat(medias.results)
        } else {
            // numbered pagination
            gallery.medias.results = medias.results
        }

        gallery.medias.pagination = medias.pagination
    },
    removeFromFavoriteGallery(state, media) {
        const gallery = state.galleries[galleryTypes.FAVORITE].find(gallery =>
            gallery.medias.results.find(m => m.id === media.id)
        )
        if (gallery) {
            gallery.media_count -= 1
            gallery.medias.results = gallery.medias.results.filter(m => m.id !== media.id)
        }
    },
    addToFavoriteGallery(state, media) {
        const gallerySource = Object.values(state.galleries).flat().find(gallery => gallery.medias.results.find(m => m.id === media.id))
        if (gallerySource) {
            const gallery = state.galleries[galleryTypes.FAVORITE].find(gallery => gallery.id === gallerySource.id)
            if (gallery) {
                gallery.media_count += 1
                gallery.medias.results.unshift(media)
            } else {
                state.galleries[galleryTypes.FAVORITE].unshift({
                    id: gallerySource.id,
                    name: gallerySource.name,
                    layout: gallerySource.layout,
                    media_count: 1,
                    medias: {
                        results: [media],
                        pagination: {}
                    }
                })
            }
        }
    },
    updateMedia(state, {media, data}) {
        Object.values(galleryTypes).forEach(type => {
            const gallery = state.galleries[type].find(gallery =>
                gallery.medias.results.find(m => m.id === media.id)
            );
            if (gallery) {
                const mediaIndex = gallery.medias.results.findIndex(m => m.id === media.id);
                if (mediaIndex !== -1) {
                    gallery.medias.results[mediaIndex] = {
                        ...gallery.medias.results[mediaIndex],
                        ...data,
                    };
                    // Force reactivity
                    gallery.medias.results = [...gallery.medias.results];
                }
            }
        });
    },
    resetGalleries(state) {
        state.galleries = {}
    }
}

export const actions = (mediaService) => ({
        async fetchGalleries(context, {checkin_id}) {
            const response = await mediaService.getGalleries(checkin_id)
            context.commit('setGalleries', response)
            return response
        },
        async fetchMediasByGallery(context, {gallery_type, checkin_id, gallery_id, append, params}) {
            const response = await mediaService.getMediasByGallery(checkin_id, gallery_id, params)
            context.commit('setMedias', {gallery_type, gallery_id, append, medias: response})
            return response
        },
        async toggleFavorite(context, media) {
            if (media.favorite) {
                mediaService.removeFavorite(media.id)
                context.commit('removeFromFavoriteGallery', media)
            } else {
                mediaService.addFavorite(media.id)
                context.commit('addToFavoriteGallery', media)
            }
            context.commit('updateMedia', {media, data: {favorite: !media.favorite}})
            return media.favorite
        },
        resetGalleries(context) {
            context.commit('resetGalleries')
        },
        async checkNewMedia(context, {checkin_id}) {
            // Fetch fresh galleries data to compare counters
            const response = await mediaService.getGalleries(checkin_id)
            const currentGalleries = context.getters.galleries
            
            let newMediaCount = 0
            let changedGalleries = []
            
            // Compare each gallery type
            Object.values(galleryTypes).forEach(type => {
                if (response[type] && currentGalleries[type]) {
                    response[type].forEach(newGallery => {
                        const currentGallery = currentGalleries[type].find(g => g.id === newGallery.id)
                        
                        if (currentGallery && newGallery.media_count > currentGallery.media_count) {
                            const diff = newGallery.media_count - currentGallery.media_count
                            newMediaCount += diff
                            changedGalleries.push({
                                id: newGallery.id,
                                name: newGallery.name || 'Gallery',
                                newCount: diff,
                                type: type
                            })
                        } else if (!currentGallery) {
                            newMediaCount += newGallery.media_count
                            changedGalleries.push({
                                id: newGallery.id,
                                name: newGallery.name || 'Gallery',
                                newCount: newGallery.media_count,
                                type: type
                            })
                        }
                    })
                } else if (response[type] && !currentGalleries[type]) {
                    response[type].forEach(newGallery => {
                        newMediaCount += newGallery.media_count
                        changedGalleries.push({
                            id: newGallery.id,
                            name: newGallery.name || 'Gallery',
                            newCount: newGallery.media_count,
                            type: type
                        })
                    })
                }
            })
            
            // Check if we currently have any media displayed
            const currentTotalMedia = Object.values(currentGalleries).flat().reduce((acc, gallery) => acc + gallery.media_count, 0)
            
            let shouldAutoRefresh = false
            let shouldShowPopup = false
            
            if (newMediaCount > 0) {
                if (currentTotalMedia === 0) {
                    // No media currently shown - auto refresh without popup
                    shouldAutoRefresh = true
                    shouldShowPopup = false
                } else {
                    // Media already shown - show popup but don't auto refresh
                    shouldAutoRefresh = false
                    shouldShowPopup = true
                }
            }
            
            // Update the store with fresh data only if auto-refreshing
            if (shouldAutoRefresh) {
                context.commit('setGalleries', response)
            }
            
            return {
                hasNewMedia: newMediaCount > 0,
                totalNewMedia: newMediaCount,
                changedGalleries: changedGalleries,
                freshData: response,
                shouldAutoRefresh: shouldAutoRefresh,
                shouldShowPopup: shouldShowPopup
            }
        }
    }
)


export default mediaService => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(mediaService)
})